import axios from 'axios';
import { authApi } from '../Auth/userSession';
import { toConceptualizationResult, validConceptualization } from '../Practice/AIClients/aiClientData';

const LOGIN_REQUIRED_MESSAGE = '로그인 후 이용할 수 있습니다. 로그인해주세요.';
const SERVER_ERROR_MESSAGE = '서버에 문제가 생겼어요. 문의하기에서 문의주세요.';

const PROFILE_ERROR_MESSAGES = {
    400: '입력한 정보를 다시 확인해주세요.',
    401: LOGIN_REQUIRED_MESSAGE,
};

const PASSWORD_ERROR_MESSAGES = {
    400: '현재 비밀번호가 맞지 않거나 새 비밀번호가 기준에 맞지 않습니다.',
    401: LOGIN_REQUIRED_MESSAGE,
    403: '현재 비밀번호가 일치하지 않습니다.',
};

const WITHDRAW_ERROR_MESSAGES = {
    400: '비밀번호를 입력해주세요.',
    401: LOGIN_REQUIRED_MESSAGE,
    403: '비밀번호가 일치하지 않습니다.',
};

const SOCIAL_ERROR_MESSAGES = {
    401: LOGIN_REQUIRED_MESSAGE,
    501: '소셜 계정 연결은 아직 준비 중입니다.',
};

// 보안상 서버 오류 내용은 화면에 보여주지 않고 상태 코드별 안내 문구로 바꾼다.
function apiErrorMessage(error, messages) {
    if (process.env.NODE_ENV !== 'production') console.error(error);
    if (axios.isCancel(error)) return LOGIN_REQUIRED_MESSAGE;
    const status = error.response?.status;
    if (messages[status]) return messages[status];
    if (!error.response) return '서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.';
    return SERVER_ERROR_MESSAGE;
}

// 응답에 user 객체가 있는지 확인하고, 없으면 서버 오류로 처리한다.
function requireUser(data) {
    if (!data?.user || typeof data.user !== 'object') {
        if (process.env.NODE_ENV !== 'production') console.error('마이페이지 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return data.user;
}

// 마이페이지 기본 정보를 조회한다. GET /api/mypage
export async function getMyPage() {
    let data;
    try {
        ({ data } = await authApi.get('/api/mypage'));
    } catch (error) {
        throw new Error(apiErrorMessage(error, { 401: LOGIN_REQUIRED_MESSAGE }));
    }
    return {
        user: requireUser(data),
        hasPassword: Boolean(data.hasPassword),
        socialAccounts: Array.isArray(data.socialAccounts) ? data.socialAccounts : [],
    };
}

// 기본 정보(이름·생년월일·성별·소속)를 수정하고 갱신된 user를 돌려준다. PATCH /api/mypage
export async function updateProfile({ name, birthDate, gender, affiliation }) {
    let data;
    try {
        ({ data } = await authApi.patch('/api/mypage', { name, birthDate, gender, affiliation }));
    } catch (error) {
        throw new Error(apiErrorMessage(error, PROFILE_ERROR_MESSAGES));
    }
    return requireUser(data);
}

// 비밀번호를 변경한다. 성공 시 서버가 모든 refresh 토큰을 폐기한다. POST /api/mypage/password
export async function changePassword({ currentPassword, password }) {
    try {
        await authApi.post('/api/mypage/password', { currentPassword, password });
    } catch (error) {
        throw new Error(apiErrorMessage(error, PASSWORD_ERROR_MESSAGES));
    }
}

// 최근 로그인 기록을 조회한다. GET /api/mypage/login-history
export async function getLoginHistory() {
    let data;
    try {
        ({ data } = await authApi.get('/api/mypage/login-history'));
    } catch (error) {
        throw new Error(apiErrorMessage(error, { 401: LOGIN_REQUIRED_MESSAGE }));
    }
    if (!Array.isArray(data?.items)) {
        if (process.env.NODE_ENV !== 'production') console.error('로그인 기록 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return data.items;
}

// 회원 탈퇴(계정 비활성화)를 요청한다. 비밀번호가 있는 계정만 password를 보낸다. DELETE /api/mypage
export async function deleteAccount({ password } = {}) {
    try {
        await authApi.delete('/api/mypage', { data: password ? { password } : {} });
    } catch (error) {
        throw new Error(apiErrorMessage(error, WITHDRAW_ERROR_MESSAGES));
    }
}

// 소셜 계정 요청을 보내고 오류를 안내 문구로 바꾼다. 현재 서버는 라우터만 있어 501을 돌려준다.
async function requestSocial(method, provider, body) {
    try {
        const { data } = await authApi.request({ method, url: `/api/mypage/social/${encodeURIComponent(provider)}`, data: body });
        return data;
    } catch (error) {
        throw new Error(apiErrorMessage(error, SOCIAL_ERROR_MESSAGES));
    }
}

// 소셜 계정 연결을 시작한다. GET /api/mypage/social/:provider
// TODO: 서버가 302 리다이렉트로 구현되면 startNaverLogin처럼 browser.location.assign 방식으로 바꾼다(axios로는 외부 리다이렉트를 따라갈 수 없음).
export function startSocialLink(provider) {
    return requestSocial('get', provider);
}

// 소셜 인증 결과로 계정 연결을 완료한다. POST /api/mypage/social/:provider
export function completeSocialLink(provider, body) {
    return requestSocial('post', provider, body);
}

// 소셜 계정 연결을 해제한다. DELETE /api/mypage/social/:provider
export function unlinkSocial(provider) {
    return requestSocial('delete', provider);
}

const ACTIVITY_ERROR_MESSAGES = {
    401: LOGIN_REQUIRED_MESSAGE,
    403: '해당 면담 기록을 볼 수 없습니다.',
    404: '면담 기록을 찾을 수 없습니다.',
};

const FEEDBACK_ERROR_MESSAGES = {
    ...ACTIVITY_ERROR_MESSAGES,
    404: '이 면담의 피드백 리포트가 아직 없습니다.',
};

// AI 내담자 면담 활동 목록을 조회한다. GET /api/mypage/ai-client-activity
export async function getAiClientActivity() {
    let data;
    try {
        ({ data } = await authApi.get('/api/mypage/ai-client-activity'));
    } catch (error) {
        throw new Error(apiErrorMessage(error, ACTIVITY_ERROR_MESSAGES));
    }
    const items = Array.isArray(data) ? data : data?.items;
    if (!Array.isArray(items)) {
        if (process.env.NODE_ENV !== 'production') console.error('활동 목록 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return items;
}

// 면담 대화록을 조회한다. 응답은 { instanceId, clientName, status, messages: [{ id, role, speaker, content, createdAt }] } 등. GET /api/mypage/ai-client-activity/:instanceId/chat
export async function getAiClientChat(instanceId) {
    let data;
    try {
        ({ data } = await authApi.get(`/api/mypage/ai-client-activity/${encodeURIComponent(instanceId)}/chat`));
    } catch (error) {
        throw new Error(apiErrorMessage(error, ACTIVITY_ERROR_MESSAGES));
    }
    if (!Array.isArray(data?.messages)) {
        if (process.env.NODE_ENV !== 'production') console.error('대화록 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return data;
}

// 면담 피드백 리포트를 조회한다. 아직 만들지 않았으면(404) null. 응답은 기존 종합 피드백 분석 결과와 같다. GET /api/mypage/ai-client-activity/:instanceId/feedback
export async function getAiClientFeedback(instanceId) {
    let data;
    try {
        ({ data } = await authApi.get(`/api/mypage/ai-client-activity/${encodeURIComponent(instanceId)}/feedback`));
    } catch (error) {
        if (error.response?.status === 404) return null;
        throw new Error(apiErrorMessage(error, FEEDBACK_ERROR_MESSAGES));
    }
    return requireFeedbackReport(data);
}

// 피드백 리포트 응답 형식을 확인하고, 다르면 서버 오류로 처리한다.
function requireFeedbackReport(data) {
    if (!data?.strengthsImprovements) {
        if (process.env.NODE_ENV !== 'production') console.error('피드백 리포트 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return data;
}

const FEEDBACK_REQUEST_ERROR_CODES = {
    SUPERVISION_MODEL_UNSUPPORTED: '현재 종합 피드백은 동기면담(MI) 관점으로 준비한 면담만 받을 수 있습니다.',
    CHAT_NOT_COMPLETED: '면담을 마친 뒤 종합 피드백을 받을 수 있습니다.',
};
const FEEDBACK_REQUEST_ERROR_MESSAGES = {
    ...ACTIVITY_ERROR_MESSAGES,
    429: '피드백 요청이 많습니다. 잠시 후 다시 시도해주세요.',
};

// 면담 피드백 리포트를 새로 만든다(분석·저장). 이미 있으면 서버가 저장된 결과를 돌려준다. POST /api/mypage/ai-client-activity/:instanceId/feedback
export async function requestAiClientFeedback(instanceId) {
    let data;
    try {
        ({ data } = await authApi.post(`/api/mypage/ai-client-activity/${encodeURIComponent(instanceId)}/feedback`, {}, {
            // 분석에 시간이 걸릴 수 있어 기본보다 길게 기다린다.
            timeout: 180000,
        }));
    } catch (error) {
        // 오류 코드는 화면 문구를 고르는 데만 쓰고 그대로 보여주지 않는다.
        const code = error.response?.data?.code || error.response?.data?.error;
        if (FEEDBACK_REQUEST_ERROR_CODES[code]) {
            if (process.env.NODE_ENV !== 'production') console.error(error);
            throw new Error(FEEDBACK_REQUEST_ERROR_CODES[code]);
        }
        throw new Error(apiErrorMessage(error, FEEDBACK_REQUEST_ERROR_MESSAGES));
    }
    return requireFeedbackReport(data);
}

// 면담의 사례개념화(항목 글, 항목별 피드백, 연결 점검)를 조회한다. 작성하지 않았으면 null. GET /api/mypage/ai-client-activity/:instanceId/conceptualization
export async function getAiClientConceptualization(instanceId) {
    let data;
    try {
        ({ data } = await authApi.get(`/api/mypage/ai-client-activity/${encodeURIComponent(instanceId)}/conceptualization`));
    } catch (error) {
        if (error.response?.status === 404 && error.response.data?.code === 'ANALYSIS_NOT_FOUND') return null;
        throw new Error(apiErrorMessage(error, ACTIVITY_ERROR_MESSAGES));
    }
    if (!validConceptualization(data)) {
        if (process.env.NODE_ENV !== 'production') console.error('사례개념화 조회 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return toConceptualizationResult(data);
}

const INQUIRY_LIST_ERROR_MESSAGES = { 401: LOGIN_REQUIRED_MESSAGE };

// 내가 등록한 문의 목록을 조회한다. GET /api/inquiry/list
export async function getInquiryList() {
    let data;
    try {
        ({ data } = await authApi.get('/api/inquiry/list'));
    } catch (error) {
        throw new Error(apiErrorMessage(error, INQUIRY_LIST_ERROR_MESSAGES));
    }
    const items = Array.isArray(data) ? data : data?.items;
    if (!Array.isArray(items)) {
        if (process.env.NODE_ENV !== 'production') console.error('문의 목록 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return items;
}

const INQUIRY_CREATE_ERROR_MESSAGES = {
    400: '제목과 내용을 다시 확인해주세요.',
    401: LOGIN_REQUIRED_MESSAGE,
};

// 문의를 등록한다. POST /api/inquiry
export async function createInquiry({ title, content }) {
    try {
        await authApi.post('/api/inquiry', { title, content });
    } catch (error) {
        throw new Error(apiErrorMessage(error, INQUIRY_CREATE_ERROR_MESSAGES));
    }
}

const INQUIRY_DETAIL_ERROR_MESSAGES = {
    401: LOGIN_REQUIRED_MESSAGE,
    403: '문의 내역을 확인할 수 없습니다.',
    404: '문의 내역을 찾을 수 없습니다.',
};

// 내 문의 하나를 상세 조회한다. GET /api/inquiry/:id
export async function getInquiry(id) {
    let data;
    try {
        ({ data } = await authApi.get(`/api/inquiry/${encodeURIComponent(id)}`));
    } catch (error) {
        throw new Error(apiErrorMessage(error, INQUIRY_DETAIL_ERROR_MESSAGES));
    }
    const inquiry = data?.inquiry || data;
    if (!inquiry?.title) {
        if (process.env.NODE_ENV !== 'production') console.error('문의 상세 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return inquiry;
}
